import {
  NORMALIZED_ZOOMED_OUT,
  setNormalizedZoomLevel,
  setCanvasFocusPositionAndScale,
  getScale,
} from './canvas';
import { SHADOWS } from './constants';
import { COLORS } from './constants-colors';
import { cacheImage } from './controllers/data_controller';
import logger from './controllers/logger';
import {
  getZeroEntity,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
  getObjectsByType,
  getFirstObjectByType,
  getZeroAnchoredEntity,
  anchoredPos,
  getEntityPositionXY,
  moveEntityToBack,
  moveEntityToFront,
} from './entity';
import { v2 } from './helpers';
import { createImageEnt } from './image_entity';
import { createJourneyMap } from './journey_map';
import { createPhotoChoice } from './photo_choice';
import { createAndAddLocalImageObject, getAndClearLastClicked } from './image_object';
import { initRoom } from './room';
import { setVisibilityState } from './simulate';
import { createAndAddSlider, setSliderScaledValue } from './slider';
import { createAndAddLocalTextObject2 } from './text_object2/text_object2';
import { t } from './localizationController';
import { ObjectType } from './shared/api_shared_types';
import {
  MoveMode,
  SliderOrientation,
  VisibilityState,
  type GameManager,
  type ImageEnt,
  type ImageObject,
  type Room,
  type Slider,
  type TextObject2,
} from './types';

let room: Room = null;
let sceneManager: GameManager = null;
let isZoomedIn = false;
let isPendingLock = false;
let savedNormalizedZoom = 0;
let savedScale = 0;
let savedLayerX = 0;
let savedLayerY = 0;
let focusedPhoto: ImageObject | null = null;
let photoDescText: TextObject2 | null = null;

// Canvas zoom level (0..100 → scale 0.2..8, exponential) when clicking a photo to inspect it.
const PHOTO_ZOOM_LEVEL = 45;

// Pool of photos the repeating PhotoChoice cycles through, and how many it shows at once.
const PHOTOS_PER_CHOICE = 3;
const PHOTO_CHOICE_POOL = [
  'DSCF2213',
  'DSCF2258',
  'DSCF2505',
  'DSCF2580',
  'DSCF2732',
  'DSCF2849',
  'DSCF2883',
  'DSCF2972',
  'DSCF3475',
  'DSCF3547',
  'DSCF3622',
  'DSCF3679',
  'DSCF3735',
  'DSCF5287',
  'DSCF5762',
  'DSCF5826',
  'DSCF6058',
  'DSCF6430',
  'DSC_5922',
  'IMG_0452',
  'IMG_1971',
];
// Every photo id that has already been shown in a choice — so each photo is only ever presented once.
const presentedPhotos = new Set<string>();

// Presents the next batch of not-yet-shown photos as a new PhotoChoice.
// Returns false when every photo in the pool has been presented (so the choice UI stops).
function presentNextPhotoChoice(manager: GameManager): boolean {
  const ids: string[] = [];
  for (const id of PHOTO_CHOICE_POOL) {
    if (presentedPhotos.has(id)) {
      continue;
    }
    ids.push(id);
    if (ids.length >= PHOTOS_PER_CHOICE) {
      break;
    }
  }

  if (ids.length === 0) {
    return false; //all photos have been presented — stop showing the choice UI
  }

  for (const id of ids) {
    presentedPhotos.add(id);
  }
  createPhotoChoice(manager, ids);
  return true;
}

// Creates a horizontal integer-snapping slider as canvas content, showing `labels[i]`
// under each notch. The notch count equals `max - min`, so pass a small step-index range
// and put the real numbers (euros, days, …) in `labels`.
function createValueSlider(
  manager: GameManager,
  min: number,
  max: number,
  labels: string[],
  x: number,
  y: number,
): Slider {
  const slider = createAndAddSlider(manager, '', SliderOrientation.HORIZONTAL, 2000);
  slider.min = min;
  slider.max = max;
  slider.snapToInt = true;
  slider.labels = labels;
  slider.barWidth = 50;
  slider.maxBarWidth = 50;

  slider.height = 100;
  slider.showCurrentLabel = false;
  slider.showValueInKnob = true;
  manager.mainRoom.canvas.attachChild(slider, manager.mainRoom.canvas);
  // attachChild pushes non-draggable children to the back; keep the slider on top.
  moveEntityToFront(slider);
  setEntityPosition(slider, x, y);
  setSliderScaledValue(slider, min, true); // start the knob at the first notch
  return slider;
}

export function init(manager: GameManager) {
  sceneManager = manager;
  room = initRoom(manager, 'wish collector', 'wishcollector1');
  setVisibilityState(room, VisibilityState.DEFAULT);

  setNormalizedZoomLevel(room.canvas, NORMALIZED_ZOOMED_OUT);

  room.width = manager.space.width;
  room.height = manager.space.height;

  manager.mainRoom = room;

  room.fixedZIndex = true;

  {
    const props: Partial<TextObject2> = {
      defaultTextStyle: {
        color: COLORS.WHITE_100,
        fontSize: 340,
        fontId: 'font_regular',
        lineHeight: 1,
      },
      // textOverflow: 'wrap',
      // width: 1800,
      text: 'Wish~Collector',
    };
    const title = createAndAddLocalTextObject2(manager, getZeroStaticEntity(), props);
    manager.mainRoom.canvas.attachChild(title, manager.mainRoom.canvas);
    setEntityPosition(title, -2600, -2000);
  }

  {
    const map = createJourneyMap(manager, getZeroStaticEntity());
    map.rotation = -0.04;
    manager.mainRoom.canvas.attachChild(map, manager.mainRoom.canvas);
    map.allowHighlight = true;
    map.allowDrag = true;
    setEntityPosition(map, -1500, 50);
  }

  {
    const props: Partial<ImageEnt> = { width: 2000 };
    const title = createImageEnt(manager, getZeroStaticEntity(), 'title', props);
    title.rotation = -0.04;
    manager.mainRoom.canvas.attachChild(title, manager.mainRoom.canvas);
    title.allowHighlight = true;
    title.allowDrag = true;
    setEntityPosition(title, -2600, -2000);
  }

  {
    const tableProps: Partial<ImageEnt> = {
      maxWidthEm: 100,
    };
    const table = createImageEnt(manager, getZeroStaticEntity(), 'table2', tableProps);
    manager.mainRoom.canvas.attachChild(table, manager.mainRoom.canvas);
    moveEntityToBack(table); // keep the table as the backmost canvas content
  }

  {
    const PHOTO_AREA_CENTER_X = 800;
    const PHOTO_AREA_CENTER_Y = 0;
    const PHOTO_AREA_WIDTH = 2000;
    const PHOTO_AREA_HEIGHT = 2000;
    const PHOTO_SEED = 42;
    const PHOTO_MAX_ROTATION = 0.08;

    const PHOTOS = [
      { file: 'DSCF2213.JPG', isEnabled: true },
      { file: 'DSCF2258.JPG', isEnabled: true },
      { file: 'DSCF2309.JPG', isEnabled: false },
      { file: 'DSCF2505.JPG', isEnabled: true },
      { file: 'DSCF2580.JPG', isEnabled: true },
      { file: 'DSCF2722.JPG', isEnabled: false },
      { file: 'DSCF2732.JPG', isEnabled: true },
      { file: 'DSCF2819.JPG', isEnabled: false },
      { file: 'DSCF2849.JPG', isEnabled: true },
      { file: 'DSCF2883.JPG', isEnabled: true },
      { file: 'DSCF2928.JPG', isEnabled: false },
      { file: 'DSCF2972.JPG', isEnabled: true },
      { file: 'DSCF3475.JPG', isEnabled: true },
      { file: 'DSCF3547.JPG', isEnabled: true },
      { file: 'DSCF3558.JPG', isEnabled: false },
      { file: 'DSCF3622.JPG', isEnabled: true },
      { file: 'DSCF3679.JPG', isEnabled: true },
      { file: 'DSCF3735.JPG', isEnabled: true },
      { file: 'DSCF3823.JPG', isEnabled: false },
      { file: 'DSCF5287.JPG', isEnabled: true },
      { file: 'DSCF5586.JPG', isEnabled: false },
      { file: 'DSCF5762.JPG', isEnabled: true },
      { file: 'DSCF5826.JPG', isEnabled: true },
      { file: 'DSCF5967.JPG', isEnabled: false },
      { file: 'DSCF6058.JPG', isEnabled: true },
      { file: 'DSCF6430.JPG', isEnabled: true },
      { file: 'DSCF6579.JPG', isEnabled: false },
      { file: 'DSC_5922.JPG', isEnabled: true },
      { file: 'IMG_0452.JPG', isEnabled: true },
      { file: 'IMG_1971.JPG', isEnabled: true },
    ];

    let s = PHOTO_SEED;
    const rand = () => {
      s = (s * 1664525 + 1013904223) & 0xffffffff;
      return (s >>> 0) / 0xffffffff;
    };

    if (false) {
      for (const photo of PHOTOS) {
        const rx = rand();
        const ry = rand();
        const rr = rand();

        if (!photo.isEnabled) {
          continue;
        }

        const imageId = photo.file.slice(0, photo.file.lastIndexOf('.'));
        cacheImage('images/photos/' + photo.file, imageId);

        const photoProps = {
          maxImageWidthEm: 7,
          maxImageHeightEm: 7,
          label: '',
          appearDuration: 0.4,
          influenceKinetic: true,
          moveMode: MoveMode.KINETIC,
        };
        const photoObj = createAndAddLocalImageObject(
          manager,
          getZeroEntity(),
          imageId,
          photoProps,
        );
        manager.mainRoom.canvas.attachChild(photoObj, manager.mainRoom.canvas);

        const x = PHOTO_AREA_CENTER_X + (rx - 0.5) * PHOTO_AREA_WIDTH;
        const y = PHOTO_AREA_CENTER_Y + (ry - 0.5) * PHOTO_AREA_HEIGHT;
        setEntityPosition(photoObj, x, y);
        const rot = (rr - 0.5) * 2 * PHOTO_MAX_ROTATION;
        photoObj.rotation = rot;
        photoObj.baseRotation = rot;
      }
    }
  }

  // Price slider: 4000 → 20000 €, in €2000 steps (euros = valueInt * 2000).
  createValueSlider(
    manager,
    2,
    10,
    ['€4000', '€6000', '€8000', '€10000', '€12000', '€14000', '€16000', '€18000', '€20000'],
    2300,
    -1800,
  );

  // Duration slider: 14 → 30 days, in 2-day steps (days = valueInt * 2).
  createValueSlider(
    manager,
    7,
    15,
    [
      '14 days',
      '16 days',
      '18 days',
      '20 days',
      '22 days',
      '24 days',
      '26 days',
      '28 days',
      '30 days',
    ],
    2300,
    -1200,
  );

  createValueSlider(
    manager,
    7,
    15,
    [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    2300,
    -1500,
  );

  // Photo-selection panel: presents photos to pick from; selected ones fly onto the canvas.
  // Once completed and gone, the update loop presents the next one.
  presentNextPhotoChoice(manager);
}

function setPhotosDraggable(isAllowed: boolean) {
  for (const photo of getObjectsByType<ImageObject>(sceneManager, ObjectType.IMAGE_OBJECT)) {
    photo.allowDrag = isAllowed;
  }
}

function updatePhotoZoom() {
  const canvas = sceneManager.mainRoom.canvas;

  if (isPendingLock) {
    if (!canvas.scrollAnimation && !canvas.zoomAnimation) {
      canvas.allowZoom = false;
      canvas.allowScrollVert = false;
      canvas.allowScrollHor = false;
      setPhotosDraggable(false);

      const descKey = `photo.${focusedPhoto.imageEnt.imageId}`;
      const descProps: Partial<TextObject2> = {
        text: t(descKey),
        isRichText: true,
        textOverflow: 'wrap',
        width: 700,
        defaultTextStyle: {
          color: COLORS.WHITE_100,
          fontSize: 32,
          fontId: 'font_regular',
          lineHeight: 1.2,
          shadow: SHADOWS.TEXT,
        },
      };
      photoDescText = createAndAddLocalTextObject2(
        sceneManager,
        getZeroAnchoredEntity(),
        descProps,
      );
      photoDescText.anchoredPos = anchoredPos(v2(0.2, 0.8), v2(0, 0), v2(0, 0.5));
      setParent(photoDescText, room.lobbyLayer);
      const [initX, initY] = getEntityPositionXY(photoDescText);
      photoDescText.pos.x = initX;
      photoDescText.pos.y = initY;
      // setEntityPosition(
      //   photoDescText,
      //   sceneManager.space.width / 2,
      //   sceneManager.space.height - 120,
      // );

      isPendingLock = false;
      isZoomedIn = true;
    }
    return;
  }

  const clicked = getAndClearLastClicked();

  // Photos attached to the journey map can always be zoomed in. Other photos (e.g. the chooser's)
  // must not drive the zoom while the photo-choice panel is open.
  const isPhotoChoiceOpen = getFirstObjectByType(sceneManager, ObjectType.PHOTO_CHOICE) != null;
  const canZoomToClicked =
    clicked != null && (!isPhotoChoiceOpen || clicked.journeyMapAnchorIndex !== null);

  if (!isZoomedIn) {
    if (canZoomToClicked) {
      savedNormalizedZoom = canvas.normalizedZoomLevel;
      savedScale = canvas.layer.scale;
      savedLayerX = canvas.layer.pos.x;
      savedLayerY = canvas.layer.pos.y;
      focusedPhoto = clicked;

      setCanvasFocusPositionAndScale(canvas, clicked.pos, 0.5, false, getScale(PHOTO_ZOOM_LEVEL));

      isPendingLock = true;
    }
  } else {
    if (clicked) {
      const restoreFp = v2(-savedLayerX / savedScale, -savedLayerY / savedScale);
      setCanvasFocusPositionAndScale(canvas, restoreFp, 0.5, false, savedScale);

      canvas.allowZoom = true;
      canvas.allowScrollVert = true;
      canvas.allowScrollHor = true;
      setPhotosDraggable(true);

      if (photoDescText) {
        setVisibilityState(photoDescText, VisibilityState.DISAPPEARING);
        photoDescText = null;
      }
      focusedPhoto = null;
      isZoomedIn = false;
    }
  }
}

export function update() {
  if (!sceneManager || !sceneManager.mainRoom) {
    return;
  }

  // Once the current choice has been made and its panel is gone, present the next one.
  if (!getFirstObjectByType(sceneManager, ObjectType.PHOTO_CHOICE)) {
    presentNextPhotoChoice(sceneManager);
  }

  updatePhotoZoom();
}
